import { ReadSyncableStateStatisticsDocument } from 'generated/resources/graphql'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { usePrivateIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'
import { toSyncParameters } from 'modules/integrations/lib/parameters'
import { usePolling } from 'modules/integrations/hooks/usePolling'
import { useCallback } from 'react'

interface UseSyncableStateStatisticsOptions {
  skip?: boolean
  pollingIntervalMs?: number | null
}

export const useSyncableStateStatistics = ({
  skip = false,
  pollingIntervalMs = null,
}: UseSyncableStateStatisticsOptions) => {
  const {
    syncEnabled,
    marketplaceIntegration,
    configuration,
    dataCollectionBaseGraphqlJsonString,
    companyId,
    contextParameters,
    isBookkeeperView,
  } = usePrivateIntegrationsSyncContext()

  const [fetchSyncableStateStatistics, { data, loading }] = useLazyQuery(
    ReadSyncableStateStatisticsDocument
  )

  const serializedContextParameters = JSON.stringify(contextParameters)

  const canFetch = !skip && !!syncEnabled && !!marketplaceIntegration && !!companyId

  const isPollingEnabled = canFetch && pollingIntervalMs != null && pollingIntervalMs > 0

  // Fetch on mount and when dependencies change.
  // usePolling only handles subsequent interval-based refreshes.
  useUnsafeEffect(() => {
    if (!canFetch) return

    const parsedContextParameters = JSON.parse(serializedContextParameters)

    fetchSyncableStateStatistics({
      variables: {
        origin: configuration.origin,
        parameters: toSyncParameters(parsedContextParameters),
        graphqlVariablesJson: dataCollectionBaseGraphqlJsonString,
        companyId,
        marketplaceIntegrationId: marketplaceIntegration.id,
      },
      context: {
        isBookkeeperView,
      },
    })
  }, [
    canFetch,
    marketplaceIntegration,
    companyId,
    isBookkeeperView,
    configuration.origin,
    serializedContextParameters,
    dataCollectionBaseGraphqlJsonString,
    fetchSyncableStateStatistics,
  ])

  const pollCallback = useCallback(() => {
    if (!companyId || !marketplaceIntegration) return

    const parsedContextParameters = JSON.parse(serializedContextParameters)

    fetchSyncableStateStatistics({
      variables: {
        companyId,
        marketplaceIntegrationId: marketplaceIntegration.id,
        origin: configuration.origin,
        parameters: toSyncParameters(parsedContextParameters),
        graphqlVariablesJson: dataCollectionBaseGraphqlJsonString,
      },
      context: {
        isBookkeeperView,
      },
      fetchPolicy: 'network-only',
    })
  }, [
    companyId,
    marketplaceIntegration,
    configuration.origin,
    serializedContextParameters,
    dataCollectionBaseGraphqlJsonString,
    isBookkeeperView,
    fetchSyncableStateStatistics,
  ])

  usePolling({
    enabled: isPollingEnabled,
    callback: pollCallback,
    intervalMs: pollingIntervalMs ?? 0,
  })

  const refetch = useCallback(() => {
    if (!canFetch) return
    pollCallback()
  }, [canFetch, pollCallback])

  const statistics = data?.integrations?.syncableStateStatisticsConnection?.nodes?.[0]
  const resolvedStatistics = canFetch ? statistics : null

  return { statistics: resolvedStatistics, loading, refetch }
}
